import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN,
  TIKTOK,
  YOUTUBE,
} from "./constants";

export const PUBLICATION_STATUS = {
  SCHEDULED: "scheduled",
  PUBLISHED: "published",
  FAILED: "failed",
};

export const PUBLICATION_SOURCE = {
  DIRECT: "direct",
  SCHEDULE: "schedule",
};

const PLATFORM_ORDER = [INSTAGRAM, FACEBOOK, TIKTOK, YOUTUBE, LINKEDIN];

const STATUS_RANK = {
  [PUBLICATION_STATUS.PUBLISHED]: 3,
  [PUBLICATION_STATUS.SCHEDULED]: 2,
  [PUBLICATION_STATUS.FAILED]: 1,
};

const isPublicationStatus = (value) =>
  value === PUBLICATION_STATUS.SCHEDULED ||
  value === PUBLICATION_STATUS.PUBLISHED ||
  value === PUBLICATION_STATUS.FAILED;

const toTimestamp = (value) => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

const readSourceNode = (value, fallbackStatus) => {
  if (value === true) {
    return isPublicationStatus(fallbackStatus)
      ? {
          status: fallbackStatus,
          scheduledAt: null,
          publishedAt: null,
          failedAt: null,
        }
      : null;
  }
  if (!value || typeof value !== "object") return null;
  const status = isPublicationStatus(value.status)
    ? value.status
    : fallbackStatus;
  if (!isPublicationStatus(status)) return null;
  return {
    status,
    scheduledAt: toTimestamp(value.scheduledAt),
    publishedAt: toTimestamp(value.publishedAt),
    failedAt: toTimestamp(value.failedAt),
  };
};

const pickPreferredStatus = (candidates = []) =>
  candidates.reduce((best, current) => {
    if (!best) return current;
    const rankDiff =
      (STATUS_RANK[current.status] || 0) - (STATUS_RANK[best.status] || 0);
    if (rankDiff > 0) return current;
    if (rankDiff < 0) return best;
    if (
      current.source === PUBLICATION_SOURCE.DIRECT &&
      best.source !== PUBLICATION_SOURCE.DIRECT
    ) {
      return current;
    }
    return best;
  }, null);

const comparePlatformStatuses = (left, right) => {
  const leftIndex = PLATFORM_ORDER.indexOf(left.platform);
  const rightIndex = PLATFORM_ORDER.indexOf(right.platform);
  const platformDiff =
    (leftIndex === -1 ? PLATFORM_ORDER.length : leftIndex) -
    (rightIndex === -1 ? PLATFORM_ORDER.length : rightIndex);
  if (platformDiff !== 0) return platformDiff;
  return String(left.accountID).localeCompare(String(right.accountID));
};

export const mapContentPlatformStatuses = ({ publishedSources } = {}) => {
  const rows = [];
  const sourcesRoot =
    publishedSources && typeof publishedSources === "object"
      ? publishedSources
      : {};

  Object.entries(sourcesRoot).forEach(([platform, accounts]) => {
    Object.entries(accounts || {}).forEach(([accountID, accountSources]) => {
      const candidates = [];
      const direct = readSourceNode(
        accountSources?.direct,
        PUBLICATION_STATUS.PUBLISHED
      );
      if (direct) {
        candidates.push({
          platform,
          accountID,
          status: direct.status,
          source: PUBLICATION_SOURCE.DIRECT,
          scheduledAt: direct.scheduledAt,
          publishedAt: direct.publishedAt,
          failedAt: direct.failedAt,
        });
      }

      Object.entries(accountSources?.schedules || {}).forEach(
        ([scheduleID, value]) => {
          const node = readSourceNode(value, PUBLICATION_STATUS.SCHEDULED);
          if (!node) return;
          candidates.push({
            platform,
            accountID,
            scheduleID,
            status: node.status,
            source: PUBLICATION_SOURCE.SCHEDULE,
            scheduledAt: node.scheduledAt,
            publishedAt: node.publishedAt,
            failedAt: node.failedAt,
          });
        }
      );

      const preferred = pickPreferredStatus(candidates);
      if (!preferred) return;
      rows.push(preferred);
    });
  });

  return rows.sort(comparePlatformStatuses);
};
